// ─── Backend client ────────────────────────────────────────────────────
// Thin fetch wrappers around the FastAPI server. Every call uses
// credentials:'include' so the httpOnly JWT cookie travels both ways.
// Throws ApiError on non-2xx so callers can surface it via the toast.

const BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000/api';

// Most endpoints send `detail` as a plain string. Pipeline validation
// errors (PipelineValidationError, server/app/engine/pipeline_validate.py)
// send a structured object instead — {error, message, violations: [...]}
// — so a bare `body?.detail` would otherwise become the literal string
// "[object Object]" once Error's constructor stringifies it. Pull out the
// human-readable message either way, and keep the raw `violations` list
// (if present) on `.violations` for a caller that wants to render it.
function detailMessage(detail) {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || null;
  return null;
}

export class ApiError extends Error {
  constructor(status, body) {
    super(detailMessage(body?.detail) || `HTTP ${status}`);
    this.status = status;
    this.body = body;
    this.violations = (body?.detail && typeof body.detail === 'object') ? body.detail.violations : null;
  }
}

async function call(method, path, body) {
  const init = {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  };
  let res;
  try {
    res = await fetch(BASE + path, init);
  } catch {
    // A network-level failure (server down/restarting, blocked origin) — the
    // browser's own message is just "Failed to fetch".
    throw new ApiError(0, { detail: `Can't reach the server at ${BASE} — is it running?` });
  }
  const text = await res.text();
  const json = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, json);
  return json;
}

function safeJson(s) {
  try { return JSON.parse(s); } catch { return s; }
}

// ── Auth ──────────────────────────────────────────────────────────────
export const me           = () => call('GET',  '/me');
export const login        = (body) => call('POST', '/auth/login', body);
export const logout       = () => call('POST', '/auth/logout');
export const ssoLogin     = (token) => call('GET', `/auth/sso?token=${encodeURIComponent(token)}`);

// ── Orgs ──────────────────────────────────────────────────────────────
export const listOrgs     = () => call('GET',  '/orgs');
export const switchOrg    = (organizationId) => call('POST', '/orgs/switch', { organizationId });

// ── Projects ──────────────────────────────────────────────────────────
export const listProjects = () => call('GET',  '/projects');
export const createProject = (body) => call('POST', '/projects', body);
export const updateProject = (id, body) => call('PUT', `/projects/${id}`, body);
export const deleteProject = (id) => call('DELETE', `/projects/${id}`);

// ── Pipelines ─────────────────────────────────────────────────────────
export const createPipeline = (projectId, body) =>
  call('POST', `/projects/${projectId}/pipelines`, body);
export const updatePipeline = (id, body) => call('PUT', `/pipelines/${id}`, body);
export const deletePipeline = (id) => call('DELETE', `/pipelines/${id}`);
export const duplicatePipeline = (id) => call('POST', `/pipelines/${id}/duplicate`);
export const patchSchedule = (id, body) => call('PATCH', `/pipelines/${id}/schedule`, body);

// ── Engine ────────────────────────────────────────────────────────────
export const preview  = (pipelineId, body) => call('POST', `/pipelines/${pipelineId}/preview`, body);
export const testStep = (pipelineId, body) => call('POST', `/pipelines/${pipelineId}/test-step`, body);

// A step's full, uncapped result (Data Preview's "Download full result"
// button) — a real file download, not JSON, so this bypasses call() and
// reads the response as a blob. Returns {blob, filename}; filename comes
// from the server's Content-Disposition header (see routers/runs.py's
// step_download_filename) so the caller doesn't have to rebuild it.
export async function downloadStepResult(pipelineId, stepId, body) {
  const res = await fetch(BASE + `/pipelines/${pipelineId}/steps/${stepId}/download`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text ? safeJson(text) : null);
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match ? match[1] : `download.${body?.format === 'xlsx' ? 'xlsx' : 'csv'}`;
  const blob = await res.blob();
  return { blob, filename };
}

// ── Input file storage ────────────────────────────────────────────────
export async function uploadInputFile(pipelineId, file, sheetName, blankBuildTarget, buildTargetName) {
  const form = new FormData();
  form.append('file', file);
  // Which sheet of a multi-sheet workbook is this pipeline's build target —
  // required by the upload UI whenever there's more than one sheet to choose
  // from (see preview/Preview.jsx's confirm()). Omitted for CSV/single-sheet
  // uploads, where there's nothing to choose.
  if (sheetName) form.append('sheet_name', sheetName);
  // The chosen build target is a BLANK sheet, not one inside the uploaded
  // workbook — there's nothing for the server to parse for the primary
  // input, so tell it to skip trying rather than send a sheet_name that
  // will never exist in the file.
  if (blankBuildTarget) form.append('blank_build_target', 'true');
  // The build target's user-assigned display name (raw sheet name for a
  // real target may have been renamed in the naming UI; a blank target has
  // no other name at all) — stored purely so SourceSelect's "Self" option
  // can label itself with it. Never used to resolve what "Self" reads.
  if (buildTargetName) form.append('build_target_name', buildTargetName);
  const res = await fetch(BASE + `/pipelines/${pipelineId}/upload-input`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await res.text();
  const json = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, json);
  return json;
}

export const getInputData = (pipelineId) =>
  call('GET', `/pipelines/${pipelineId}/input-data`);
// Drop the stored input file + its sheets — used when the new input carries no
// uploaded bytes, so the PREVIOUS file can't keep being served as a fallback.
export const clearInputFile = (pipelineId) =>
  call('DELETE', `/pipelines/${pipelineId}/input-file`);

// ── Sheets (additional named sources from a multi-sheet upload) ────────
export const listSheets  = (pipelineId) => call('GET', `/pipelines/${pipelineId}/sheets`);
export const upsertSheet = (pipelineId, name, body) =>
  call('PUT', `/pipelines/${pipelineId}/sheets/${encodeURIComponent(name)}`, body);
export const deleteSheet = (pipelineId, name) =>
  call('DELETE', `/pipelines/${pipelineId}/sheets/${encodeURIComponent(name)}`);
export const clearSheets = (pipelineId) =>
  call('DELETE', `/pipelines/${pipelineId}/sheets`);

// ── Runs ──────────────────────────────────────────────────────────────
export const runPipeline = (pipelineId, body) => call('POST', `/pipelines/${pipelineId}/runs`, body);
export const listRuns    = (pipelineId) => call('GET', `/pipelines/${pipelineId}/runs`);
export const runOutputUrl = (runId) => `${BASE}/runs/${runId}/output`;

// ── Code mode ─────────────────────────────────────────────────────────
// Checks one expression and evaluates it on the (<= 200) rows the client
// already holds; never reruns the pipeline. See server/app/code_expr/preview.py.
export const httpTest = (pipelineId, body) => call('POST', `/pipelines/${pipelineId}/http-test`, body);
export const exprPreview = (pipelineId, body) => call('POST', `/pipelines/${pipelineId}/expr-preview`, body);

// Convert a Case When block's dropdown logic (cases, THEN values, ELSE, extra
// outputs) into an equivalent code body (or the reasons it can't be done
// exactly). Always 200 for conversion problems. See
// server/app/code_expr/convert_block.py.
export const exprConvertBlock = (pipelineId, config) => call('POST', `/pipelines/${pipelineId}/expr-convert-block`, { config });
